import {AfterViewInit, Component, EventEmitter, Input, OnInit, Output, SimpleChanges} from '@angular/core';
import {switchMap, last, tap} from 'rxjs/operators';
import {throwError} from 'rxjs';
import {UserStore} from 'src/app/modules/authentication/state/user.store';
import {API_PATH, QuestionType, RouterLinks} from '../../constants';
import {PolicyQuery} from '../../../policy/state/policy.query';
import {Policy} from '../../models/policy.model';
import {Question} from '../../models/underwriter.model';
import {GlobalQuestionService} from '../../services/global-question.service';
import {newGuid} from '../question-helper';
import {Router} from '@angular/router';
import {AuthenticationService} from '../../../authentication/service/authentication.service';
import { Browser } from '@capacitor/browser';

declare const Dropzone: any;
declare const dropzonePreviewTemplate: any;
declare var $: any;
declare function notify(): any;

export enum UploaderTypes {
  AllMedia,
  Images,
  Videos,
  Documents,
  Selfie
}

@Component({
    selector: 'app-question-upload',
    templateUrl: './question-upload.component.html',
    styleUrls: ['./question-upload.component.scss'],
    standalone: false
})
export class QuestionUploadComponent implements OnInit, AfterViewInit {

  @Input() question: Question;
  @Input() uploaderType = QuestionType.upload;
  @Input() noteText = 'Note on what to take images of';
  @Input() labelText: string; // = 'Upload Images';
  @Input() showLabel = true;
  @Input() showNoteText = false;
  @Input() showUploadLabelText = false;
  @Input() acceptedFileTypes = 'image/*,application/pdf,video/mp4,video/x-m4v,video/*';
  @Input() uploadLabelText = '<strong>Format:</strong> &lt;doc, docx, xls, xlsx, pdf, mp4&gt;&nbsp; <strong>Maximum size:</strong> &lt;150mb&gt;';
  @Input() queryParam = '';
  @Output() questionAnswer = new EventEmitter<any>();

  dropZone: any;
  fileSelectorEl = '.custom-upload--media';
  uniqueId = `question-uploader-${newGuid()}`;
  thumbPath = '';
  uploaderTypes = UploaderTypes;
  savedResources: any[] = [];
  QuestionTypes = QuestionType;
  evidenceType: string;
  private isRehydrating = false;

  constructor(
    private globalQuestionService: GlobalQuestionService,
    private authService: AuthenticationService,
    private userStore: UserStore,
    private router: Router,
    private policyQuery: PolicyQuery
    ) { }

  ngOnInit(): void {
    if (this.question) {
      this.uploaderType = this.question?.cent_aq_question_type_id;
      this.evidenceType = this.question?.ai_evidence_type;
      if (this.uploaderType === QuestionType.upload) {
        this.acceptedFileTypes = 'image/*, video/mp4, video/x-m4v, video/*, application/pdf, file/*, .doc, .docx, .xls, .xlsx';
        this.thumbPath = 'assets/img/upload.svg';
        this.uploadLabelText = '<strong>Format:</strong> &lt;pdf, jpg, jpeg, png, tiff, mp4&gt;&nbsp; <strong>Maximum size:</strong> &lt;150mb&gt;';
      } else if (this.uploaderType === QuestionType.upload_images) {
        this.acceptedFileTypes = 'image/*';
        this.thumbPath = 'assets/img/upload-image.svg';
        this.uploadLabelText = '<strong>Format:</strong> &lt;pdf, jpg, jpeg, png, tiff&gt;&nbsp; <strong>Maximum size:</strong> &lt;150mb&gt;';
      } else if (this.uploaderType === QuestionType.upload_videos) {
        this.acceptedFileTypes = 'video/mp4, video/x-m4v, video/*, audio/*';
        this.thumbPath = 'assets/img/upload-video.svg';
        this.uploadLabelText = '<strong>Format:</strong> &lt;mp4&gt;&nbsp; <strong>Maximum size:</strong> &lt;150mb&gt;';
      } else if (this.uploaderType === QuestionType.upload_selfie) {
        this.acceptedFileTypes = 'image/*, video/mp4, video/x-m4v,  video/*';
        this.thumbPath = 'assets/img/upload-selfie.svg';
        this.uploadLabelText = '<strong>Format:</strong> &lt;jpg, jpeg, png, tiff, mp4&gt;&nbsp; <strong>Maximum size:</strong> &lt;150mb&gt;';
      } else if (this.uploaderType === QuestionType.upload_documents) {
        this.fileSelectorEl = '.custom-upload--docs';
        this.acceptedFileTypes = 'application/pdf,.doc,.docx,.xls,.xlsx';
        this.thumbPath = 'assets/img/upload-documents.svg';
        this.uploadLabelText = '<strong>Format:</strong> &lt;doc, docx, xls, xlsx, pdf&gt;&nbsp; <strong>Maximum size:</strong> &lt;150mb&gt;';
      }
    }
    this.authService.refreshToken()
      .subscribe(x => {
        this.userStore.update({
          access_token: x?.access_token,
          refresh_token: x?.refresh_token
        });
      });
  }

  ngAfterViewInit(): void {
    let path = API_PATH.UPLOAD_RESOURCE;
    this.savedResources = [];
    this.dropZone = new Dropzone(`#${this.uniqueId}`, {
      autoProcessQueue: false,
      autoDiscover: false,
      acceptedFiles: this.acceptedFileTypes,
      previewTemplate: dropzonePreviewTemplate,
      thumbnailWidth: 204,
      thumbnailHeight: 204,
      maxFilesize: 150,
      url: path,
      headers: {
        Authorization: `Bearer ${this.userStore.getValue()?.access_token}`
      },
    }).on('addedfile', (file: any) => {
      if (this.isRehydrating) {
        return;
      }
      file._fileId = file?.upload?.uuid;
      if (!this.evidenceType || !(file instanceof File)) {
        this.dropZone.processFile(file);
        return;
      }
      const sessionId = (this.policyQuery.getActive() as Policy)?.session_id;
      let evidenceId: string;
      this.setFileStatus(file, 'Uploading...');
      this.globalQuestionService.getPresignedUploadUrl(sessionId, this.evidenceType, file.type).pipe(
        switchMap((presignResponse: any) => {
          evidenceId = presignResponse?.evidenceId;
          return this.globalQuestionService.uploadToS3(
            presignResponse?.upload?.url,
            presignResponse?.upload?.fields,
            file
          );
        }),
        switchMap(() => {
          this.setFileStatus(file, 'Checking document...');
          return this.globalQuestionService.validateEvidence(evidenceId, this.evidenceType);
        }),
        switchMap((validateResponse: any) => {
          //console.log('Validation response:', validateResponse);
          if (validateResponse?.is_valid !== true) {
            return throwError(() => ({ isRejection: true, response: validateResponse }));
          }
         // this.setFileStatus(file, 'Validating...');
          return this.globalQuestionService.processEvidence(evidenceId, sessionId, this.evidenceType, this.question?.cent_aq_question_id?.toString(), this.question?.ai_schema_name, this.question?.item_id);
        }),
        switchMap(() => this.globalQuestionService.pollEvidenceStatus(evidenceId).pipe(
          tap((response: any) => { this.setFileStatus(file, response?.message); }),
          last()
        ))
      ).subscribe({
        next: (statusResponse: any) => {
          //console.log('Evidence status response:', statusResponse);
          if (statusResponse?.status === 'EXTRACTING') {
            this.setFileStatus(file, '');
            this.dropZone.processFile(file);
          } else {  
            const reason = statusResponse?.rejectionReason || statusResponse?.error?.message || statusResponse?.message || 'Evidence processing failed.';
            this.dropZone.removeFile(file);
            notify()?.init({
              message: `<strong>Evidence processing failed:</strong> ${reason}`,
              icon: 'info',
              size: 'lg',
              style: 'error',
              close: true,
              speed: 350,
              duration: 6000
            });
          }
        },
        error: (error: any) => {
          if (error?.isRejection) {
            const response = error?.response;
            // vlm_skipped means a basic check (e.g. resolution) failed before the VLM content
            // check ran - response.message is already a complete, human-readable message for
            // that case. Only build the "File rejected: <reason>" wrapper when the VLM did run.
            const message = response?.vlm_skipped
              ? (response?.message || 'File did not pass validation.')
              : `<strong>File rejected:</strong> ${response?.vlm?.rejection_reason || 'File did not pass validation.'}`;
            this.dropZone.removeFile(file);
            notify()?.init({
              message,
              icon: 'info',
              size: 'lg',
              style: 'error',
              close: true,
              speed: 350,
              duration: 6000
            });
          } else {
            console.log('Evidence flow error:', error);
            this.dropZone.removeFile(file);
            notify()?.init({
              message: '<strong>File upload failed.</strong> Please try again.',
              icon: 'info',
              size: 'lg',
              style: 'error',
              close: true,
              speed: 350,
              duration: 6000
            });
          }
        }
      });
    }).on('thumbnail', (file: any) => {
      //console.log(file)
      $(file.previewElement).addClass('has-thumbnail');
      $(file.previewElement.firstChild).prop('style','cursor:pointer');
      $(file.previewElement.firstChild).click(function(){
        Browser.open({ url: `${API_PATH.UPLOAD_RESOURCE}/${file.id}` , windowName: '_self', presentationStyle: 'popover'});
      });
    }).on("sending", (file:any, xhr:any, formData:any) => {
      formData.append("metadata_json", JSON.stringify(this.queryParam));
    }).on('success', (file: any, response: any) => {
      if (!this.savedResources || !this.savedResources?.length) {
        this.savedResources = [];
      }
      this.savedResources.push({ resource_id: response, file_id: file?._fileId });
      this.question.answer = JSON.stringify(this.savedResources);
      if (this.isRehydrating) { return; }
      this.question.response_parameters = this.question.response_parameters || this.question.parameters;
      this.questionAnswer.emit(this.question);
    }).on('removedfile', (file: any) => {
      const deletedFile = this.savedResources.find(item => item?.file_id === file?._fileId);
      if (deletedFile) {
        this.savedResources = this.savedResources.filter(item => item?.file_id !== file?._fileId);
        this.removeFile(deletedFile?.resource_id);
        if (!this.savedResources || !this.savedResources?.length) {
          this.question.answer = '';
          this.questionAnswer.emit(this.question);
        } else {
          this.question.answer = JSON.stringify(this.savedResources);
          this.questionAnswer.emit(this.question);
        }
      }
    }).on('error', (file: any, response: any) => {
      console.log(response)
      if (response?.indexOf('401') !== -1) {
        this.router.navigate([RouterLinks.Login], {queryParams: {returnUrl: this.router.routerState.snapshot.url}});
      }
    });

    if (this.question?.answer) {
      try {
        const responses = JSON.parse(this.question?.answer);
        this.isRehydrating = true;
        for (const res of responses) {
          const resource_id = res?.resource_id;
          const mockFile = {
            name: res?.original_filename,
            size: res?.file_size,
            uuid: res?.file_id,
            id: resource_id,
            _fileId: res?.file_id
          };
          this.dropZone.emit('addedfile', mockFile);
          this.dropZone.emit('thumbnail', mockFile, `${API_PATH.UPLOAD_RESOURCE}/${resource_id}`);
          this.dropZone.emit('success', mockFile, resource_id);
          this.dropZone.emit('complete', mockFile);
          this.dropZone.options.thumbnail.call(this.dropZone,mockFile, res?.image);
        }
        this.isRehydrating = false;
      }
      catch (e) {
        //console.log(e)
      }
    }
    else {
      // this.question.answer = 'Not answered';
      // this.questionAnswer.emit(this.question);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.queryParam) {
      this.queryParam = changes.queryParam.currentValue;
    }
  }

  removeFile(resource_id: string): void {
    this.globalQuestionService.removeFile(resource_id);
    //this.question.answer = 'Not answered';
    //this.questionAnswer.emit(this.question);
  }

  private setFileStatus(file: any, message: string): void {
    if (file?.previewElement) {
      const statusEl = file.previewElement.querySelector('[data-dz-errormessage]') ||
                       file.previewElement.querySelector('.dz-error-message');
      if (statusEl) {
        statusEl.textContent = message;
      }
    }
  }

}
